import axios from 'axios'
import useAuthStore from '../store/authStore'

// API Configuration
// En desarrollo, usa URL relativa para aprovechar el proxy de Vite
// En producción, configura VITE_API_URL con la URL absoluta del backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

// ⚠️ SEGURIDAD: La API key no debería estar en el frontend
// En producción, el backend debería validar el token JWT en lugar de usar API key
const API_KEY = import.meta.env.VITE_API_KEY || 'fallback-key-for-dev'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds for OpenAI extraction operations
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    console.log('🔍 DEBUG - API Request Interceptor - URL:', config.url);
    console.log('🔍 DEBUG - API Request Interceptor - Token:', token ? 'Presente' : 'Ausente');
    console.log('🔍 DEBUG - API Request Interceptor - Token preview:', token ? token.substring(0, 20) + '...' : 'null');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Deshabilitar caché para evitar problemas con 304 Not Modified
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
    config.headers['Pragma'] = 'no-cache'
    config.headers['Expires'] = '0'
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    console.log('🔍 DEBUG - Axios Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    return response
  },
  (error) => {
    const { response } = error
    
    console.log('❌ DEBUG - Axios Error:', {
      url: error.config?.url,
      status: response?.status,
      data: response?.data,
      message: error.message
    });

    // Handle 401 errors (unauthorized)
    if (response?.status === 401) {
      const { logout } = useAuthStore.getState()
      logout()
      window.location.href = '/login'
      return Promise.reject(new Error('Sesión expirada. Por favor, inicia sesión nuevamente.'))
    }

    // Handle network errors
    if (!response) {
      console.error('Network error details:', {
        error: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      })
      return Promise.reject(new Error(`Error de conexión: ${error.message || 'No se pudo conectar al servidor'}`))
    }

    // Handle other HTTP errors
    const message = response.data?.message || response.data?.error || 'Error desconocido'
    return Promise.reject(new Error(message))
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verify: () => api.post('/auth/verify'),
  getMe: () => api.get('/auth/me'),
  getUsers: (params) => api.get('/auth/users', { params }),
  createUser: (userData) => api.post('/auth/users', userData),
  updateUser: (id, userData) => api.put(`/auth/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/auth/users/${id}`)
}

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (clientData) => api.post('/clients', clientData),
  update: (id, clientData) => api.put(`/clients/${id}`, clientData),
  delete: (id) => api.delete(`/clients/${id}`),
  getAccountStatus: (id, params) => api.get(`/clients/${id}/account-status`, { params })
}

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (formData) => api.post('/purchase-orders', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/purchase-orders/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  updateStatus: (id, status) => api.patch(`/purchase-orders/${id}/status`, { status }),
  getPdfUrl: (id) => api.get(`/purchase-orders/${id}/pdf`),
  extract: (formData) => api.post('/purchase-orders/extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (formData) => api.post('/invoices', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/invoices/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/invoices/${id}`),
  updateStatus: (id, status) => api.patch(`/invoices/${id}/status`, { status }),
  getPdfUrl: (id) => api.get(`/invoices/${id}/pdf`)
}

// Dashboard API
export const dashboardAPI = {
  getGlobal: (params) => api.get('/dashboard', { params }),
  getClient: (clientId, params) => api.get(`/dashboard/client/${clientId}`, { params })
}

// Reports API
export const reportsAPI = {
  getAccountStatus: (params) => api.get('/reports/account-status', { params }),
  getClientAccountStatus: (clientId, params) => api.get(`/reports/client/${clientId}/account-status`, { params }),
  getMonthlySales: (params) => api.get('/reports/monthly-sales', { params }),
  getTopDebtors: (params) => api.get('/reports/top-debtors', { params }),
  getAging: (params) => api.get('/reports/aging', { params })
}

// Notifications API
export const notificationsAPI = {
  getSettings: () => api.get('/notifications/settings'),
  getConfigs: () => {
    console.log('🔍 DEBUG - notificationsAPI.getConfigs - Iniciando petición');
    return api.get('/notifications/configs');
  },
  createConfig: (configData) => api.post('/notifications/configs', configData),
  updateConfig: (id, configData) => api.put(`/notifications/configs/${id}`, configData),
  deleteConfig: (id) => api.delete(`/notifications/configs/${id}`),
  toggleConfig: (id) => api.post(`/notifications/configs/${id}/toggle`),
  sendTest: (testData) => api.post('/notifications/test', testData),
  getLogs: (params) => api.get('/notifications/logs', { params }),
  sendWeeklyReminder: () => api.post('/notifications/send-weekly-reminder'),
  sendOverdueAlert: () => api.post('/notifications/send-overdue-alert'),
  sendMonthlyReport: () => api.post('/notifications/send-monthly-report')
}

// Health check
export const healthAPI = {
  check: () => {
    // Si API_BASE_URL es relativa (/api), usar /health directamente (el proxy lo manejará)
    // Si es absoluta, construir la URL correctamente
    const healthUrl = API_BASE_URL.startsWith('http') 
      ? `${API_BASE_URL.replace('/api', '')}/health`
      : '/health'
    return axios.get(healthUrl)
  }
}

// System configuration (no auth required)
export const configAPI = {
  getSystemConfig: () => axios.get(`${API_BASE_URL}/config`)
}

// Utility functions
export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob'
    })
    
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    throw new Error('Error al descargar el archivo')
  }
}

export const formatApiError = (error) => {
  // Custom error messages in Spanish
  const errorTranslations = {
    'Network Error': 'Error de conexión de red',
    'Request failed': 'Solicitud fallida',
    'timeout': 'Tiempo de espera agotado',
    'Failed to fetch': 'Error de conexión',
    'Connection refused': 'Conexión rechazada',
    'ECONNREFUSED': 'No se pudo conectar al servidor',
    'Request timeout': 'Tiempo de espera de solicitud agotado'
  }

  if (error.response?.data?.message) {
    return error.response.data.message
  }
  
  if (error.message) {
    // Check for common English error messages and translate them
    const lowerMessage = error.message.toLowerCase()
    for (const [englishMsg, spanishMsg] of Object.entries(errorTranslations)) {
      if (lowerMessage.includes(englishMsg.toLowerCase())) {
        return spanishMsg
      }
    }
    return error.message
  }
  
  return 'Error desconocido'
}

export default api
